import { Element, type Props } from './element';
import {
  Ammeter, Battery, Capacitor, CurrentSource, Diode, Fuse, Ground,
  Inductor, Lamp, Led, Resistor, Switch, UsbSource, VoltageSource, Voltmeter, Wire,
} from './elements';
import { Bjt, Mosfet, OpAmp } from './semis';
import { BmsModule, ChargerModule, DcDcModule } from './modules';
import { ConstantPowerLoad, LOAD_PRESETS } from './loads';
import { Port, SubcktInstance } from './subckt';
import { ClockSource, DFlipFlop, LogicGate, LogicInput, LogicOutput } from './logic';
import { DEFAULT_MCU_CODE, Microcontroller, PushButton } from './mcu';
import { registerElementFactory } from './serial';

export type FieldKind = 'number' | 'bool' | 'select' | 'color' | 'text' | 'code';

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
}

export interface ElementSpec {
  type: string;
  label: string;
  group: string;
  pinCount: number;
  /** 팔레트에서 끌어다 놓을 때의 기본 길이(격자 칸) */
  defaultLength: number;
  defaults: Props;
  fields: FieldSpec[];
  ctor: new (id: string, type: string, x1: number, y1: number, x2: number, y2: number, props: Props) => Element;
}

const R = (key: string, label: string, unit: string, min = 0): FieldSpec => ({
  key, label, kind: 'number', unit, min,
});

export const ELEMENT_SPECS: ElementSpec[] = [
  {
    type: 'wire', label: '도선', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: {}, fields: [], ctor: Wire,
  },
  {
    type: 'resistor', label: '저항', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: { resistance: 100, ratedPower: 0.25 },
    fields: [R('resistance', '저항', 'Ω', 1e-6), R('ratedPower', '정격 전력 (넘으면 뜨거워져요)', 'W', 1e-3)],
    ctor: Resistor,
  },
  {
    type: 'lamp', label: '전구', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: { resistance: 10, nominalPower: 0.5 },
    fields: [R('resistance', '저항', 'Ω', 1e-6), R('nominalPower', '정격 전력', 'W', 1e-6)],
    ctor: Lamp,
  },
  {
    type: 'switch', label: '스위치', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: { closed: false },
    fields: [{ key: 'closed', label: '닫힘', kind: 'bool' }],
    ctor: Switch,
  },
  {
    type: 'fuse', label: '퓨즈', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: { rating: 1, resistance: 0.01 },
    fields: [R('rating', '정격 전류', 'A', 1e-6), R('resistance', '저항', 'Ω', 1e-6)],
    ctor: Fuse,
  },
  {
    type: 'ground', label: '접지', group: '기본', pinCount: 1, defaultLength: 1,
    defaults: {}, fields: [], ctor: Ground,
  },

  {
    type: 'battery', label: '전지', group: '전원', pinCount: 2, defaultLength: 2,
    defaults: { emf: 1.5, internalR: 0.05, capacity: 0, initialSoc: 1 },
    fields: [
      R('emf', '기전력', 'V'),
      R('internalR', '내부저항', 'Ω', 1e-4),
      R('capacity', '용량 (0=무한)', 'mAh', 0),
      { key: 'initialSoc', label: '초기 잔량', kind: 'number', min: 0, max: 1, step: 0.01 },
    ],
    ctor: Battery,
  },
  {
    type: 'vsource', label: '전압원', group: '전원', pinCount: 2, defaultLength: 2,
    defaults: { voltage: 5, waveform: 'dc', frequency: 60, offset: 0 },
    fields: [
      R('voltage', '전압(진폭)', 'V', -1e9),
      {
        key: 'waveform', label: '파형', kind: 'select',
        options: [
          { value: 'dc', label: '직류 DC' },
          { value: 'sine', label: '사인파' },
          { value: 'square', label: '사각파' },
          { value: 'triangle', label: '삼각파' },
        ],
      },
      R('frequency', '주파수', 'Hz', 0),
      R('offset', '오프셋', 'V', -1e9),
    ],
    ctor: VoltageSource,
  },
  {
    type: 'csource', label: '전류원', group: '전원', pinCount: 2, defaultLength: 2,
    defaults: { amps: 0.01 },
    fields: [R('amps', '전류', 'A', -1e9)],
    ctor: CurrentSource,
  },

  {
    type: 'voltmeter', label: '전압계', group: '계측', pinCount: 2, defaultLength: 2,
    defaults: { resistance: 1e8 },
    fields: [R('resistance', '내부저항', 'Ω', 1e3)],
    ctor: Voltmeter,
  },
  {
    type: 'ammeter', label: '전류계', group: '계측', pinCount: 2, defaultLength: 2,
    defaults: {}, fields: [], ctor: Ammeter,
  },

  {
    type: 'capacitor', label: '커패시터', group: '소자', pinCount: 2, defaultLength: 2,
    defaults: { capacitance: 1e-5, initialV: 0 },
    fields: [R('capacitance', '용량', 'F', 1e-15), R('initialV', '초기 전압', 'V', -1e9)],
    ctor: Capacitor,
  },
  {
    type: 'inductor', label: '인덕터', group: '소자', pinCount: 2, defaultLength: 2,
    defaults: { inductance: 1e-3, initialI: 0 },
    fields: [R('inductance', '인덕턴스', 'H', 1e-12), R('initialI', '초기 전류', 'A', -1e9)],
    ctor: Inductor,
  },
  {
    type: 'diode', label: '다이오드', group: '소자', pinCount: 2, defaultLength: 2,
    defaults: { is: 1e-14, n: 1 },
    fields: [R('is', '포화전류 Is', 'A', 1e-20), R('n', '방출계수 n', '', 0.1)],
    ctor: Diode,
  },
  {
    type: 'led', label: 'LED', group: '소자', pinCount: 2, defaultLength: 2,
    defaults: { vf: 2, nominalI: 0.02, color: '#ff4d4d' },
    fields: [
      R('vf', '순방향 전압', 'V', 0.3),
      R('nominalI', '정격 전류', 'A', 1e-6),
      { key: 'color', label: '색', kind: 'color' },
    ],
    ctor: Led,
  },

  {
    type: 'npn', label: 'NPN', group: '반도체', pinCount: 3, defaultLength: 2,
    defaults: { beta: 100, is: 1e-14, flip: false },
    fields: [R('beta', '전류증폭률 β', '', 1), R('is', '포화전류 Is', 'A', 1e-20)],
    ctor: Bjt,
  },
  {
    type: 'pnp', label: 'PNP', group: '반도체', pinCount: 3, defaultLength: 2,
    defaults: { beta: 100, is: 1e-14, flip: false },
    fields: [R('beta', '전류증폭률 β', '', 1), R('is', '포화전류 Is', 'A', 1e-20)],
    ctor: Bjt,
  },
  {
    type: 'nmos', label: 'N-MOSFET', group: '반도체', pinCount: 3, defaultLength: 2,
    defaults: { vth: 2, beta: 0.5, lambda: 0.01, flip: false },
    fields: [R('vth', '문턱전압', 'V', 0), R('beta', '이득계수 β', 'A/V²', 1e-9), R('lambda', 'λ', '1/V', 0)],
    ctor: Mosfet,
  },
  {
    type: 'pmos', label: 'P-MOSFET', group: '반도체', pinCount: 3, defaultLength: 2,
    defaults: { vth: 2, beta: 0.5, lambda: 0.01, flip: false },
    fields: [R('vth', '문턱전압 (크기)', 'V', 0), R('beta', '이득계수 β', 'A/V²', 1e-9), R('lambda', 'λ', '1/V', 0)],
    ctor: Mosfet,
  },
  {
    type: 'opamp', label: '연산증폭기', group: '반도체', pinCount: 3, defaultLength: 4,
    defaults: { gain: 1e5, vpos: 12, vneg: -12, flip: false },
    fields: [R('gain', '개방 이득', '', 1), R('vpos', '+ 전원 레일', 'V', -1e9), R('vneg', '− 전원 레일', 'V', -1e9)],
    ctor: OpAmp,
  },

  {
    type: 'usb', label: 'USB 전원', group: '모듈', pinCount: 2, defaultLength: 2,
    defaults: { emf: 5, internalR: 0.2, capacity: 0, initialSoc: 1 },
    fields: [R('emf', '전압', 'V'), R('internalR', '내부저항', 'Ω', 1e-4)],
    ctor: UsbSource,
  },
  {
    type: 'liion', label: '리튬이온 셀', group: '모듈', pinCount: 2, defaultLength: 2,
    defaults: { chemistry: 'liion', emf: 3.7, internalR: 0.05, capacity: 2000, initialSoc: 0.5 },
    fields: [
      R('capacity', '용량', 'mAh', 1),
      R('internalR', '내부저항', 'Ω', 1e-4),
      { key: 'initialSoc', label: '초기 잔량 (0~1)', kind: 'number', min: 0, max: 1, step: 0.01 },
    ],
    ctor: Battery,
  },
  {
    type: 'charger', label: '충전 모듈', group: '모듈', pinCount: 4, defaultLength: 4,
    defaults: { chargeCurrent: 1, cvVoltage: 4.2, termination: 0.1, minInput: 4.4, rs: 0.02, flip: false },
    fields: [
      R('chargeCurrent', '충전 전류', 'A', 0),
      R('cvVoltage', '만충 전압', 'V', 0),
      R('termination', '완료 기준 전류', 'A', 0),
      R('minInput', '최소 입력 전압', 'V', 0),
    ],
    ctor: ChargerModule,
  },
  {
    type: 'dcdc', label: 'DC-DC 컨버터', group: '모듈', pinCount: 4, defaultLength: 4,
    defaults: { vout: 5, imax: 2, efficiency: 0.9, uvlo: 2.9, rout: 0.05, flip: false },
    fields: [
      R('vout', '출력 전압', 'V', 0),
      R('imax', '최대 전류', 'A', 0),
      { key: 'efficiency', label: '효율 (0~1)', kind: 'number', min: 0.05, max: 1, step: 0.01 },
      R('uvlo', '입력 차단 전압', 'V', 0),
    ],
    ctor: DcDcModule,
  },
];

const VDD: FieldSpec = { key: 'vdd', label: '전원 전압 VDD', kind: 'number', unit: 'V', min: 0.5 };
const gate = (type: string, label: string): ElementSpec => ({
  type, label, group: '로직', pinCount: type === 'not' ? 2 : 3, defaultLength: type === 'not' ? 3 : 4,
  defaults: { vdd: 5, flip: false }, fields: [VDD], ctor: LogicGate,
});
ELEMENT_SPECS.push(
  { type: 'logicin', label: '논리 입력', group: '로직', pinCount: 1, defaultLength: 1,
    defaults: { on: false, vdd: 5 }, fields: [{ key: 'on', label: '1 (High)', kind: 'bool' }, VDD], ctor: LogicInput },
  { type: 'logicout', label: '논리 표시', group: '로직', pinCount: 1, defaultLength: 1,
    defaults: { vdd: 5 }, fields: [VDD], ctor: LogicOutput },
  { type: 'clock', label: '클럭', group: '로직', pinCount: 1, defaultLength: 1,
    defaults: { frequency: 1, vdd: 5 }, fields: [R('frequency', '주파수', 'Hz', 1e-6), VDD], ctor: ClockSource },
  gate('and', 'AND'), gate('or', 'OR'), gate('not', 'NOT'),
  gate('nand', 'NAND'), gate('nor', 'NOR'), gate('xor', 'XOR'),
  { type: 'dff', label: 'D 플립플롭', group: '로직', pinCount: 4, defaultLength: 4,
    defaults: { vdd: 5, flip: false }, fields: [VDD], ctor: DFlipFlop },
  { type: 'mcu', label: '마이크로컨트롤러', group: '로직', pinCount: 8, defaultLength: 6,
    defaults: { code: DEFAULT_MCU_CODE, tickMs: 1, flip: false },
    fields: [
      { key: 'code', label: '프로그램 (JavaScript)', kind: 'code' },
      R('tickMs', 'loop() 주기', 'ms', 0.1),
    ],
    ctor: Microcontroller },
  { type: 'button', label: '푸시 버튼', group: '기본', pinCount: 2, defaultLength: 2,
    defaults: {}, fields: [], ctor: PushButton },
);

ELEMENT_SPECS.push(
  {
    type: 'bms', label: '보호회로', group: '모듈', pinCount: 4, defaultLength: 4,
    defaults: { ovc: 4.3, uvd: 2.5, ocp: 4, ron: 0.03, flip: false },
    fields: [
      R('ovc', '과충전 차단 전압', 'V', 0),
      R('uvd', '과방전 차단 전압', 'V', 0),
      R('ocp', '과전류 차단', 'A', 0),
      R('ron', 'MOSFET 저항', 'Ω', 1e-3),
    ],
    ctor: BmsModule,
  },
  {
    type: 'cpload', label: '전자기기 부하', group: '모듈', pinCount: 2, defaultLength: 2,
    defaults: { preset: 'screen', power: 1, vmin: 2.8, enabled: true },
    fields: [
      {
        key: 'preset', label: '종류', kind: 'select',
        options: Object.entries(LOAD_PRESETS).map(([value, p]) => ({
          value, label: value === 'custom' ? p.label : `${p.label} ${p.watts}W`,
        })),
      },
      R('power', '전력 (직접 입력일 때)', 'W', 0),
      R('vmin', '최소 동작 전압', 'V', 0),
      { key: 'enabled', label: '켜짐', kind: 'bool' },
    ],
    ctor: ConstantPowerLoad,
  },
  {
    type: 'port', label: '포트 (단자)', group: '모듈 만들기', pinCount: 1, defaultLength: 1,
    defaults: { name: 'P1', side: 'left' },
    fields: [
      { key: 'name', label: '이름', kind: 'text' },
      {
        key: 'side', label: '모듈 상자에서 위치', kind: 'select',
        options: [{ value: 'left', label: '왼쪽' }, { value: 'right', label: '오른쪽' }],
      },
    ],
    ctor: Port,
  },
  {
    // 팔레트에는 '내 모듈' 로 따로 보인다
    type: 'subckt', label: '모듈', group: '_hidden', pinCount: 0, defaultLength: 6,
    defaults: { defId: '', name: '모듈', ports: '[]', flip: false },
    fields: [],
    ctor: SubcktInstance,
  },
);

for (const spec of ELEMENT_SPECS) {
  if ((spec.pinCount > 2 || spec.type === 'subckt' || spec.type === 'not') && spec.type !== 'mcu') spec.fields.push({ key: 'flip', label: '반전 (F 키)', kind: 'bool' });
}

export const SPEC_BY_TYPE: Record<string, ElementSpec> = Object.fromEntries(
  ELEMENT_SPECS.map((s) => [s.type, s]),
);

export const ELEMENT_GROUPS: string[] = [...new Set(ELEMENT_SPECS.map((s) => s.group))];

let idCounter = 0;
export function nextId(): string {
  idCounter += 1;
  return `e${idCounter}`;
}
export function bumpIdCounter(ids: string[]): void {
  for (const id of ids) {
    const m = /^e(\d+)$/.exec(id);
    if (m) idCounter = Math.max(idCounter, parseInt(m[1], 10));
  }
}

export function createElement(
  type: string,
  x1: number, y1: number, x2: number, y2: number,
  props?: Props,
  id?: string,
): Element {
  const spec = SPEC_BY_TYPE[type];
  if (!spec) throw new Error(`알 수 없는 소자 종류: ${type}`);
  const merged: Props = { ...spec.defaults, ...(props ?? {}) };
  const el = new spec.ctor(id ?? nextId(), type, x1, y1, x2, y2, merged);
  el.resetState();
  return el;
}

registerElementFactory({ create: createElement, bumpIds: bumpIdCounter });
